/**
 * Minimal shape for one payout cell (e.g. hourly, overtime) when calculating employee shift amount.
 * Used by invoice employee_items in sisko and la-forge.
 */
export interface IEmployeeShiftPayoutCell {
  readonly amount?: number;
  readonly hours?: number;
  readonly wage?: number;
}

/**
 * Minimal shape for an employee shift / employee_items row when calculating total amount.
 * Any object with these optional keys can be passed (e.g. IInvoice employee_items row).
 */
export interface IEmployeeShiftRowForAmount {
  readonly hourly?: IEmployeeShiftPayoutCell;
  readonly overtime?: IEmployeeShiftPayoutCell;
  readonly doubletime?: IEmployeeShiftPayoutCell;
  readonly pto?: IEmployeeShiftPayoutCell;
  readonly vacation?: IEmployeeShiftPayoutCell;
  readonly sick?: IEmployeeShiftPayoutCell;
  readonly holiday?: IEmployeeShiftPayoutCell;
  readonly piecework?: IEmployeeShiftPayoutCell;
  readonly correction?: { readonly amount?: number };
  readonly meal_break?: {
    readonly units?: number;
    readonly amount_per_unit?: number;
  };
  readonly rest_break?: {
    readonly units?: number;
    readonly amount_per_unit?: number;
  };
  readonly flat?: { readonly amount?: number };
}

const PAYOUT_KEYS: ReadonlyArray<
  keyof Pick<
    IEmployeeShiftRowForAmount,
    | "hourly"
    | "overtime"
    | "doubletime"
    | "pto"
    | "vacation"
    | "sick"
    | "holiday"
    | "piecework"
  >
> = [
  "hourly",
  "overtime",
  "doubletime",
  "pto",
  "vacation",
  "sick",
  "holiday",
  "piecework",
];

/**
 * Calculates the total amount for a single employee shift row (one entry in invoice employee_items).
 * Used for invoice breakdown, PDF generation, and daily updates so all calculations stay in sync.
 * Returns the same unit as the input (e.g. cents if amounts are in cents).
 */
export function getEmployeeShiftAmount(
  row: IEmployeeShiftRowForAmount
): number {
  let payoutAmount = 0;
  for (const key of PAYOUT_KEYS) {
    const payout = row[key] as IEmployeeShiftPayoutCell | undefined;
    if (!payout) continue;
    const amt = payout.amount ?? (payout.hours ?? 0) * (payout.wage ?? 0);
    payoutAmount += amt;
  }
  const correctionAmount = row.correction?.amount ?? 0;
  const mealBreakAmount =
    (row.meal_break?.units ?? 0) * (row.meal_break?.amount_per_unit ?? 0);
  const restBreakAmount =
    (row.rest_break?.units ?? 0) * (row.rest_break?.amount_per_unit ?? 0);
  const flatAmount = row.flat?.amount ?? 0;
  return (
    payoutAmount +
    correctionAmount +
    mealBreakAmount +
    restBreakAmount +
    flatAmount
  );
}
